import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getDataWithToken, postDataWithToken } from '../../api/api';
import { MEMBERSHIP, IMAGE_API_URL, GET_USER_GAMES, GET_DAILY_BMI_DATA, GAMEPASS, GET_MUVER_REWARD } from '../../helpers/Constant';
import moment from 'moment';
import BackButton from '../../common/backButton';

const WEEK_ONE_ROW = [
    { type: 'day', n: 1 },
    { type: 'day', n: 2 },
    { type: 'day', n: 3 },
    { type: 'day', n: 4 },
    { type: 'day', n: 5 },
    { type: 'day', n: 6 },
    { type: 'muver', milestone: 7 },
];


function UserRetention({ userData, piggyBankLimit, sessionHistoryClosedSignal, streakModalClosedSignal, onUserRefresh }) {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showRetentionModal, setShowRetentionModal] = useState(false);
    const [showNewGameModal, setShowNewGameModal] = useState(false);
    const [showNewMuverModal, setShowNewMuverModal] = useState(false);
    const [newlyUnlockedGame, setNewlyUnlockedGame] = useState(null);
    const [totalTime, setTotalTime] = useState(0);
    const [totalTimeUnit, setTotalTimeUnit] = useState('Seconds');
    const [totalActivePoints, setTotalActivePoints] = useState(0);
    const [totalCalorieBurn, setTotalCalorieBurn] = useState(0);

    const today = moment();
    const todayKey = today.format('YYYY-MM-DD');

    const userKey = userData?._id || userData?.id || userData?.userId;
    const hasUserData = !!userData && !!userKey;

    const isInRetentionFlow = !userData?.userAccount?.isRetentionFlowCompleted && !userData?.userAccount?.isPrimeUser;

    const onUserRefreshRef = useRef(onUserRefresh);
    const unlockInFlightRef = useRef(false);
    useEffect(() => {
        onUserRefreshRef.current = onUserRefresh;
    }, [onUserRefresh]);

    const retentionModalSeenKey = `retention-modal-seen-${userKey}`;
    const unlockModalSeenKey = `retention-new-game-modal-seen-${userKey}`;
    const muverRewardModalSeenKey = `retention-muver-reward-modal-seen-${userKey}`;

    const streakDay = useMemo(() => {
        const n = Number(userData?.userStreaks?.dailyStreak);
        if (!Number.isFinite(n) || n < 1) return 0;
        return Math.floor(n);
    }, [userData?.userStreaks?.dailyStreak]);

    const titleDay = isInRetentionFlow ? Math.min(streakDay, 7) : streakDay;

    const weekRows = useMemo(() => {
        if (isInRetentionFlow) {
            return [WEEK_ONE_ROW.map((slot) => (
                slot.type === 'day'
                    ? { ...slot }
                    : { ...slot, milestone: 7 }
            ))];
        }
        const normalizedStreak = Math.max(1, streakDay || 1);
        const currentWeekIndex = Math.floor((normalizedStreak - 1) / 7);
        const startDay = currentWeekIndex * 7 + 1;
        return [
            WEEK_ONE_ROW.map((slot) => (
                slot.type === 'day'
                    ? { ...slot, n: startDay + (slot.n - 1) }
                    : { ...slot, milestone: startDay + 6 }
            ))
        ];
    }, [streakDay, isInRetentionFlow]);

    const displayStreakDay = isInRetentionFlow ? Math.min(streakDay, 7) : streakDay;

    function LoadingDotsIcon() {
        return (
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='4' viewBox='0 0 16 4' fill='currentColor' className='text-warning position-relative z-1'>
                <circle cx='2' cy='2' r='2' transform='translate(0.464 0.238)' />
                <circle cx='2' cy='2' r='2' transform='translate(6.464 0.238)' opacity='0.46' />
                <circle cx='2' cy='2' r='2' transform='translate(12.464 0.238)' opacity='0.15' />
            </svg>
        );
    }

    const dayWrapperClass = (dayNum, calendar) => {
        if (dayNum < displayStreakDay) return 'day reward_collected text-center day_completed';
        if (dayNum === displayStreakDay) {
            return calendar ? 'day reward_collected text-center day_in_progress' : 'day reward_collected text-center day_active';
        }
        return 'day reward_collected text-center';
    };

    const ringValue = (dayNum) => {
        if (dayNum < displayStreakDay) return 100;
        return 0;
    };

    const currentWeekProgress = isInRetentionFlow
        ? displayStreakDay
        : ((streakDay - 1) % 7) + 1;
    const daysLeftToWeekReward = isInRetentionFlow
        ? Math.max(0, 8 - displayStreakDay)
        : Math.max(0, 8 - currentWeekProgress);
    const renderDayCell = (dayNum, calendar) => {

        const completed = dayNum < displayStreakDay;
        const active = dayNum === displayStreakDay;

        return (
            <div key={dayNum} className={dayWrapperClass(dayNum, calendar)}>
                <div className='day_name fw-light mb-2'>Day {dayNum}</div>
                <div
                    className='date d-flex align-items-center justify-content-center rounded-circle position-relative'
                    role='progressbar'
                    aria-valuemin='0'
                    aria-valuemax='100'
                    style={{ '--value': `${ringValue(dayNum)}` }}
                >
                    {completed && <FaCheckCircle className='text-warning position-relative z-1' />}
                    {active && <LoadingDotsIcon />}
                </div>
            </div>
        );
    };

    const muverWrapperClass = (milestone, calendar) => {
        const done = isInRetentionFlow
            ? displayStreakDay > milestone
            : (milestone === 31 ? streakDay > 30 : streakDay > milestone);
        const active = isInRetentionFlow
            ? displayStreakDay === milestone
            : (milestone === 31 ? streakDay === 31 : streakDay === milestone);
        if (done) return 'day reward_collected text-center day_completed';

        if (active) {
            return calendar
                ? 'day reward_collected text-center day_in_progress'
                : 'day reward_collected text-center day_active';
        }
        return 'day reward_collected text-center';
    };

    const renderMuverCell = (milestone, calendar) => {
        const treatAsDone = isInRetentionFlow
            ? displayStreakDay > milestone
            : (milestone === 31 ? streakDay > 30 : streakDay > milestone);
        const treatAsActive = isInRetentionFlow
            ? displayStreakDay === milestone
            : (milestone === 31 ? streakDay === 31 : streakDay === milestone);
        return (
            <div key={`muver-${milestone}`} className={muverWrapperClass(milestone, calendar)}>
                <div className='day_name fw-light mb-2'>Muver</div>
                <div
                    className='date d-flex align-items-center justify-content-center rounded-circle position-relative'
                    role='progressbar'
                    aria-valuemin='0'
                    aria-valuemax='100'
                    style={{ '--value': `${treatAsDone ? 100 : 0}` }}
                >
                    {treatAsDone && <FaCheckCircle className='text-warning position-relative z-1' />}
                    {treatAsActive && !treatAsDone && <LoadingDotsIcon />}
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!isInRetentionFlow || showRetentionModal || streakModalClosedSignal) return;
        const lastSeenDate = localStorage.getItem(retentionModalSeenKey);
        if (lastSeenDate !== todayKey) {
            setShowRetentionModal(true);
        }
    }, [streakModalClosedSignal, isInRetentionFlow]);


    const grantMuverReward = async () => {
        if (!sessionHistoryClosedSignal || !hasUserData || streakDay < 7 || streakDay % 7 !== 0 || localStorage.getItem(muverRewardModalSeenKey+'-'+streakDay) === todayKey) return;

        try {
            const response = await getDataWithToken(GET_MUVER_REWARD);
            // getDataWithToken returns response.data.data on success ({ streak, reward }), not { status, data }
            if (response?.status && response.status !== 200) return;
            if (!response?.reward) return;

            localStorage.setItem(muverRewardModalSeenKey + '-' + streakDay, todayKey);
            if (onUserRefreshRef.current) await onUserRefreshRef.current();
        } catch (error) {}
    };

    useEffect(() => {
        if (streakDay < 8 || !sessionHistoryClosedSignal) return;

        if (localStorage.getItem(muverRewardModalSeenKey + '-' + streakDay) === todayKey) return;
        grantMuverReward();
    }, [sessionHistoryClosedSignal]);

    useEffect(() => {
        if (!isInRetentionFlow || displayStreakDay > 7 || !sessionHistoryClosedSignal || showNewGameModal) return;

        const unlockDailyGame = async () => {
            if (localStorage.getItem(unlockModalSeenKey) === todayKey) {
                grantMuverReward();
                return;
            }
            if (unlockInFlightRef.current) return;
            unlockInFlightRef.current = true;
            try {
                const userGames = await getDataWithToken(GET_USER_GAMES);
                const upcoming = userGames?.upcomingUnlockedGame;
                if (!upcoming?.unlocksAt) {
                    grantMuverReward();
                    return;
                }
                // Only show for a game that unlocks in the future (next retention day)
                if (!moment(upcoming.unlocksAt).isValid() || moment(upcoming.unlocksAt).isSameOrBefore(moment())) {
                    grantMuverReward();
                    return;
                }

                localStorage.setItem(unlockModalSeenKey, todayKey);
                setNewlyUnlockedGame({
                    gameId: upcoming.gameId,
                    gameTitle: upcoming.gameTitle,
                    gameImage: upcoming.gameImage,
                    day: Math.min(displayStreakDay + 1, 7),
                    unlocksAt: upcoming.unlocksAt,
                    expiresAt: upcoming.expiresAt ?? null,
                });
                setShowNewGameModal(true);
            } catch (error) {
            } finally {
                unlockInFlightRef.current = false;
            }
        };

        unlockDailyGame();
    }, [sessionHistoryClosedSignal]);

    const handleRetentionClose = () => {
        localStorage.setItem(retentionModalSeenKey, todayKey);
        setShowRetentionModal(false);
    };

    const fetchData = async () => {
        const activityData = await postDataWithToken(GET_DAILY_BMI_DATA);

        if (activityData.data && activityData.data.length > 0) {
            let totalTime = 0;
            let totalActivePoints = 0;
            let totalCalorieBurn = 0;
            activityData.data.forEach((data) => {
                if(data.physicalActivity > 0) totalTime += data.physicalActivity;
                if(data.totalScore > 0) totalActivePoints += data.totalScore;
                if(data.burned > 0) totalCalorieBurn += data.burned;
            });

            let totalTimeUnit = 'Seconds';
            if(totalTime > 0) totalTime = totalTime / 60;
            if (totalTime >= 60) {
                totalTime = (totalTime / 60).toFixed(1);
                totalTimeUnit = 'Hours';
            } else {
                totalTime = Math.round(totalTime);
                totalTimeUnit = 'Minutes';
            }
            setTotalTime(totalTime);
            setTotalTimeUnit(totalTimeUnit);
            setTotalActivePoints(totalActivePoints);
            setTotalCalorieBurn(totalCalorieBurn);
        }
    }

    const handleClaimNewGame = () => {
        setShowNewGameModal(false);
        if(streakDay === 5 || streakDay === 7) {
            setShowNewMuverModal(true);
        }
    };

    const handleNewMuverClose = async () => {
        await grantMuverReward();
        navigate(`/${GAMEPASS}`);
    };

    useEffect(() => {
        if(showNewMuverModal) return;
        fetchData();
    }, [showNewMuverModal]);

    if (!hasUserData || !isInRetentionFlow) return null;
    
    return (
        <>
            {(showNewMuverModal) && <BackButton label={t('Back')} closeFunction={handleNewMuverClose} />}

            {/* User Retention Modal */}
            <div className={`modal ${showRetentionModal ? 'show' : ''}`} style={{ display: showRetentionModal ? 'block' : 'none' }}>
                <div className='modal-dialog modal-dialog-centered' role='document'>
                    <div className='modal-content modal_content user_retention_modal'>
                        <div className='modal-body p-0'>
                            <h4 className='user_retention_title fw-bold my-3 text-white text-center'>Your Day {titleDay} Starts Now</h4>
                            <div className='user_retention_subtitle fw-light px-1 py-2 text-center'>Everyday You Muve, You Get Stronger</div>
                            <div className='user_retention_weekly my-4'>
                                {weekRows.map((weekRow, weekIndex) => {
                                    const weekStart = weekIndex * 7 + 1;
                                    return (
                                        <div
                                            key={`week-${weekStart}`}
                                            className={`user_retention_week position-relative d-flex gap-2`}
                                        >
                                            <div className='daily_reward d-flex justify-content-around align-items-center flex-fill'>
                                                {weekRow.map((slot) =>
                                                    slot.type === 'day'
                                                        ? renderDayCell(slot.n, true)
                                                        : renderMuverCell(slot.milestone, true)
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className='giftbox_reward position-relative pb-2 overflow-hidden'>
                                    <div className='giftbox_round_wrapper position-absolute top-50'>
                                        <div className='giftbox_round giftbox_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='giftbox_round giftbox_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='giftbox_round giftbox_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='giftbox_round giftbox_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='giftbox_round giftbox_round_5 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                    </div>
                                    <div className='position-relative d-flex align-items-start justify-content-between gap-1 mt-1'>
                                        <div className={`giftbox_title fw-medium mx-auto ${daysLeftToWeekReward === 1 ? 'lh-1 ps-1' : ''}`}>
                                            {daysLeftToWeekReward === 1 ? (
                                                <>Reward unlocks after your first game session.</>
                                            ) : (
                                                <>
                                                    {daysLeftToWeekReward} Day{daysLeftToWeekReward === 1 ? '' : 's'} Left
                                                    <br />
                                                    To Get A Reward
                                                </>
                                            )}
                                        </div>
                                        <img className='me-2' src='../images/giftbox.png' alt='Gift Box' />
                                    </div>
                                </div>
                            </div>
                            <div className='text-center my-3'>
                                <button className='cta_button bg-warning border-0' onClick={handleRetentionClose}>
                                    Lets Move
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`modal show`} style={{ display: 'none' }}>
                <div className='modal-dialog modal-dialog-centered' role='document'>
                    <div className='modal-content modal_content stats_update_modal p-3 pb-4'>
                        <div className='modal-body p-0'>
                            <h4 className='fw-bold mb-4 text-white text-center'>Stats Update</h4>
                            <div className='stats_update_head d-flex justify-content-around text-center mb-4'>
                                <div className='stats_update_head_col'>
                                    <div className='stats_update_head_label fw-medium mb-2'>Minutes</div>
                                    <div className='stats_update_head_text text-white'>14</div>
                                </div>
                                <div className='border-start opacity-50'></div>
                                <div className='stats_update_head_col'>
                                    <div className='stats_update_head_label fw-medium mb-2'>Active Poits</div>
                                    <div className='stats_update_head_text text-white'>1200</div>
                                </div>
                                <div className='border-start opacity-50'></div>
                                <div className='stats_update_head_col'>
                                    <div className='stats_update_head_label fw-medium mb-2'>Calorie Burn</div>
                                    <div className='stats_update_head_text text-white'><img className='me-2' src='../images/fireicon.svg' height={32} alt='fireicon' /> 255</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* New Game Modal */}
            <div className={`modal ${showNewGameModal ? 'show' : ''}`} style={{ display: showNewGameModal ? 'block' : 'none' }}>
                <div className='modal-dialog modal-dialog-centered' role='document'>
                    <div className='modal-content modal_content bg-transparent game_unlocked_modal'>
                        <div className='modal-body p-0 text-center'>
                            <h4 className='fw-light mb-5 text-uppercase'>New Game <br /><strong className='text-warning'>Unlocked</strong></h4>
                            <div className='game_unlocked_subtitle fw-light py-1 mb-3'>Day-{newlyUnlockedGame?.day || streakDay} Game</div>
                            <div className='game_unlocked_image pb-2 mb-5'>
                                <img src={newlyUnlockedGame?.gameImage ? `${IMAGE_API_URL}${newlyUnlockedGame.gameImage}` : '../images/game-slice-master.jpg'} alt='Game' width={270} />
                            </div>
                            <button className='cta_button bg-warning border-0' onClick={handleClaimNewGame}>Claim</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* New Muver Modal */}
            <div className={`modal ${showNewMuverModal ? 'show' : ''}`} style={{ display: showNewMuverModal ? 'block' : 'none' }}>
                <div className='modal-dialog modal-dialog-centered' role='document'>
                    <div className='modal-content modal_content new_muver_modal'>
                        <div className='modal-body p-0 text-center'>
                            <div className='new_muver_round_wrapper position-absolute start-50'>
                                <div className='new_muver_round new_muver_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                <div className='new_muver_round new_muver_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                <div className='new_muver_round new_muver_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                <div className='new_muver_round new_muver_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                            </div>
                            <h4 className='fw-light my-4'>You are a new <br /><strong className='text-warning text-uppercase'>MUVER</strong></h4>
                            <div className='new_muver_subtitle fw-light mb-4'>You Muved {streakDay} Days In A Row</div>
                            <div className='new_muver_stats rounded-corners px-3 py-1 mb-5'>
                                <div className='stats_title fw-bold mb-3 text-white'>Your Stats</div>
                                <div className='stats_head d-flex justify-content-around text-center mb-4'>
                                    <div className='stats_head_col'>
                                        <div className='stats_head_label fw-medium mb-2'>{totalTimeUnit}</div>
                                        <div className='stats_head_text text-white'>{totalTime}</div>
                                    </div>
                                    <div className='border-start opacity-50'></div>
                                    <div className='stats_head_col'>
                                        <div className='stats_head_label fw-medium mb-2'>Active Points</div>
                                        <div className='stats_head_text text-white'>{totalActivePoints}</div>
                                    </div>
                                    <div className='border-start opacity-50'></div>
                                    <div className='stats_head_col'>
                                        <div className='stats_head_label fw-medium mb-2'>Calorie Burn</div>
                                        <div className='stats_head_text text-white'><img className='me-2' src='../images/fireicon.svg' height={30} alt='fireicon' /> {totalCalorieBurn}</div>
                                    </div>
                                </div>
                            </div>
                            <div className='new_muver_question fw-light mb-2 py-2 text-white'>Ready to go next step?</div>
                            <button className='cta_button bg-warning border-0' onClick={() => navigate(`/${MEMBERSHIP}`)}>Get Muve Elite</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`modal show`} style={{ display: 'none' }}>
                <div className='modal-dialog modal-dialog-centered' role='document'>
                    <div className='modal-content modal_content bg-black piggy_bank_modal'>
                        <div className='modal-body text-center'>
                            <div className='piggy_bank_heading_wrapper position-relative pb-1'>
                                <div className='piggy_bank_heading position-relative z-1'>
                                    <h1 className='piggy_bank_title text-uppercase fw-bold my-0'>{t('Piggy Bank')}</h1>
                                    <img className='img-fluid' src='../images/piggy-bank.gif' alt='Piggy Bank' width={200} />
                                </div>
                                <div className='piggy_bank_round_wrapper position-absolute top-50 start-50 translate-middle'>
                                    <div className='piggy_bank_round piggy_bank_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                    <div className='piggy_bank_round piggy_bank_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                    <div className='piggy_bank_round piggy_bank_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                    <div className='piggy_bank_round piggy_bank_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                </div>
                            </div>
                            <div className='piggy_bank_count_box position-relative mx-auto mt-2'>
                                <div className='piggy_bank_count fw-bold text-start'>
                                    <span className='text-gold'>{userData?.userAccount?.piggyBankAmount}</span>
                                    <span className='text-white'>/{piggyBankLimit}</span>
                                </div>
                                <div className='piggy_bank_progressbar_track position-relative w-100'>
                                    <div className='piggy_bank_progressbar_thumb position-absolute start-0 top-0 bottom-0 mw-100 rounded-1' style={{ width: `${isNaN(userData?.userAccount?.piggyBankAmount / piggyBankLimit * 100) ? 0 : Math.round(userData?.userAccount?.piggyBankAmount / piggyBankLimit * 100)}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {(showRetentionModal || showNewGameModal || showNewMuverModal) && <div className='modal-backdrop fade show'></div>}
        </>
    );
}

export default UserRetention;